import { useFormik, FormikProps } from "formik";
import * as Yup from "yup";
import { TripFormValues } from "../types/tripTypes";
import { useAppSelector } from "store";
import { toast } from "react-toastify";
import { createTrip } from "services/createTripService";
import { useNavigate } from "react-router";

const useTripForm = () => {
  const navigate = useNavigate();
  const userDetails =
    useAppSelector((state) => state.appLayout.userDetails) || ({} as any);
  const initialValues: TripFormValues = {
    title: "",
    intro: "",
    merchantId: userDetails?.userId,
    merchantName: userDetails?.organisationDetails?.brandName,
    companyName: userDetails?.organisationDetails?.legal_name || "N/A",
    tripScope: "domestic",
    location: {
      destination: [{ country: "India", state: "", district: "" }],
      startLoc: [{ country: "India", state: "", district: "" }],
      endLoc: [{ country: "India", state: "", district: "" }],
    },
    minimumAge: 18,
    maximumAge: 20,
    group: "",
    tripType: "",
    faq: [{ id: 1, question: "", answer: "" }],
    descriptionSections: [
      { id: 1, heading: "", subHeading: "", description: "" },
    ],
    tripApprovalStatus: "Images/Slot Pending",
    comments: "",
    slotCount: 0,
    nights: 0,
    days: 0,
    youtube: [],
    inclusion: [
      { id: 0, label: "Transportation", description: "" },
      {
        id: 1,
        label: "Food",
        description: "",
      },
      { id: 2, label: "Accommodation", description: "" }
    ],
    weekend:false,
    exclusion:"",
    thingsToCarry:""
  };

  // For domestic trips country/state/district are all required.
  // For international trips only the country is required (country-only selection).
  const locationItemSchema = (requireStateDistrict: boolean) =>
    Yup.object({
      country: Yup.string().trim().required("Country is required"),
      state: requireStateDistrict
        ? Yup.string().trim().required("State is required")
        : Yup.string().nullable(),
      district: requireStateDistrict
        ? Yup.string().trim().required("District is required")
        : Yup.string().nullable(),
    });

  // International start/end need country + state + auto-selected airport (no district).
  const intlStartEndItem = Yup.object({
    country: Yup.string().trim().required("Country is required"),
    state: Yup.string().trim().required("State is required"),
    airport: Yup.string().trim().required("Airport is required"),
    district: Yup.string().nullable(),
  });

  // Domestic: country (India) + state + district for all three location groups.
  const domesticLocation = Yup.object({
    destination: Yup.array().of(locationItemSchema(true)),
    startLoc: Yup.array().of(locationItemSchema(true)),
    endLoc: Yup.array().of(locationItemSchema(true)),
  });

  // International: destination = country only; start/end = country + state + airport.
  const internationalLocation = Yup.object({
    destination: Yup.array().of(locationItemSchema(false)),
    startLoc: Yup.array().of(intlStartEndItem),
    endLoc: Yup.array().of(intlStartEndItem),
  });

  const validationSchema = Yup.object({
    merchantId: Yup.string().required("Select a merchant"),
    title: Yup.string().trim().required("Trip title is required"),
    intro: Yup.string().trim().required("Trip intro is required"),
    tripScope: Yup.string().trim().required("Trip scope is required"),
    location: Yup.object().when("tripScope", {
      is: "international",
      then: () => internationalLocation,
      otherwise: () => domesticLocation,
    }),
    minimumAge: Yup.number()
      .required("Minimum age is required")
      .min(1, "Minimum age should be at least 1"),
    maximumAge: Yup.number()
      .required("Maximum age is required")
      .max(90, "Maximum age cannot exceed 90")
      .moreThan(
        Yup.ref("minimumAge"),
        "Maximum age must be greater than minimum age"
      ),
    group: Yup.string().trim().required("Group type is required"),
    tripType: Yup.string().trim().required("Trip type is required"),
    faq: Yup.array().of(
      Yup.object({
        question: Yup.string().trim().required("FAQ question is required"),
        answer: Yup.string().trim().required("FAQ answer is required"),
      })
    ),
    descriptionSections: Yup.array().of(
      Yup.object({
        heading: Yup.string().trim().required("Heading is required"),
        subHeading: Yup.string().trim().required("Subheading is required"),
        description: Yup.string().trim().required("Description is required"),
      })
    ),
    nights: Yup.number().required("Nights is required"),
    days: Yup.number().required("Days is required"),
    inclusion: Yup.array().of(
      Yup.object({
        id: Yup.number().required(),
        label: Yup.string().required(),
        description: Yup.string()
          .trim()
          .required("Inclusion description is required"),
      })
    ),
    exclusion: Yup.string().trim().required("Exclusion is required"),
  });

  const formik: FormikProps<TripFormValues> = useFormik<TripFormValues>({
    initialValues,
    validationSchema,
    validateOnBlur: true,
    onSubmit: async (values, { setSubmitting }) => {

      try {
        const response = await createTrip(values);
        if (response.message === "Item created successfully") {
          toast.success("Trip created successfully");
          formik.resetForm();
          navigate("/trips-created");
        }
      } catch (error) {
        console.error("Error submitting trip data:", error);
        toast.error("Failed to submit trip data");
      } finally {
        setSubmitting(false);
      }
    },
  });

  return { formik };
};

export default useTripForm;
